import React, { createContext, useContext, ReactNode } from 'react';
import {
    LoginRequest,
    CurrentUserResponse,
    AuthState,
    AuthError
} from '../types/auth';
import { useAuth, UseAuthReturn } from '../hooks/useAuth';
import { debugLog } from '../utils/debug';

// ============================================================================
// CONTEXT TYPES
// ============================================================================

export interface AuthContextType extends UseAuthReturn { }

// ============================================================================
// CONTEXT CREATION
// ============================================================================

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

// ============================================================================
// PROVIDER COMPONENT
// ============================================================================

export interface AuthProviderProps {
    children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
    const auth = useAuth();

    // Log when AuthProvider mounts/unmounts (only in development)
    React.useEffect(() => {
        // debugLog('[AuthProvider] Mounted', { timestamp: new Date().toISOString() });
        return () => {
            // debugLog('[AuthProvider] Unmounting', { timestamp: new Date().toISOString() });
        };
    }, []);

    return (
        <AuthContext.Provider value={auth}>
            {children}
        </AuthContext.Provider>
    );
};

// ============================================================================
// HOOKS
// ============================================================================

/**
 * Hook to access authentication context
 * @returns Authentication context value
 * @throws Error if used outside of AuthProvider
 */
export function useAuthContext(): AuthContextType {
    const context = useContext(AuthContext);

    if (context === undefined) {
        throw new Error('useAuthContext must be used within an AuthProvider');
    }

    return context;
}

/**
 * Hook to get authentication state
 * @returns Authentication state
 */
export function useAuthState(): AuthState {
    const authContext = useAuthContext();

    return {
        isAuthenticated: authContext.isAuthenticated,
        user: authContext.user,
        accessToken: authContext.accessToken,
        refreshToken: authContext.refreshToken,
        loading: authContext.loading,
        error: authContext.error,
    };
}

/**
 * Hook to get authentication actions
 * @returns Authentication actions
 */
export function useAuthActions() {
    const {
        login,
        logout,
        refreshTokenAction,
        getCurrentUser,
        clearError,
        checkAuthStatus
    } = useAuthContext();

    return {
        login,
        logout,
        refreshTokenAction,
        getCurrentUser,
        clearError,
        checkAuthStatus,
    };
}

/**
 * Hook to check if user is authenticated
 * @returns Boolean indicating authentication status
 */
export function useIsAuthenticated(): boolean {
    const { isAuthenticated } = useAuthContext();
    return isAuthenticated;
}

/**
 * Hook to get current user
 * @returns Current user or null
 */
export function useCurrentUser(): CurrentUserResponse | null {
    const { user } = useAuthContext();
    return user;
}

/**
 * Hook to check if user has specific role
 * @param requiredRole - The role to check for
 * @returns Boolean indicating if user has the role
 */
export function useHasRole(requiredRole: string): boolean {
    const { user } = useAuthContext();
    return user?.role === requiredRole;
}

/**
 * Hook to check if user has any of the specified roles
 * @param requiredRoles - Array of roles to check for
 * @returns Boolean indicating if user has any of the roles
 */
export function useHasAnyRole(requiredRoles: string[]): boolean {
    const { user } = useAuthContext();
    return user ? requiredRoles.includes(user.role) : false;
}

/**
 * Hook to get loading state
 * @returns Boolean indicating if auth operation is in progress
 */
export function useAuthLoading(): boolean {
    const { loading } = useAuthContext();
    return loading;
}

/**
 * Hook to get error state
 * @returns Error message or null
 */
export function useAuthError(): string | null {
    const { error } = useAuthContext();
    return error;
}

// ============================================================================
// HIGHER-ORDER COMPONENTS
// ============================================================================

/**
 * HOC to require authentication
 * @param Component - Component to wrap
 * @param fallback - Component to render if not authenticated
 * @returns Wrapped component
 */
export function withAuth<P extends object>(
    Component: React.ComponentType<P>,
    fallback?: React.ComponentType
) {
    return function AuthenticatedComponent(props: P) {
        const { isAuthenticated, loading } = useAuthContext();

        if (loading) {
            return <div>Loading...</div>;
        }

        if (!isAuthenticated) {
            if (fallback) {
                return React.createElement(fallback);
            }
            return <div>Please log in to access this page.</div>;
        }

        return React.createElement(Component, props);
    };
}

/**
 * HOC to require specific role
 * @param Component - Component to wrap
 * @param requiredRole - Required role
 * @param fallback - Component to render if user doesn't have role
 * @returns Wrapped component
 */
export function withRole<P extends object>(
    Component: React.ComponentType<P>,
    requiredRole: string,
    fallback?: React.ComponentType
) {
    return function RoleProtectedComponent(props: P) {
        const { isAuthenticated, user, loading } = useAuthContext();

        if (loading) {
            return <div>Loading...</div>;
        }

        if (!isAuthenticated) {
            return <div>Please log in to access this page.</div>;
        }

        if (user?.role !== requiredRole) {
            if (fallback) {
                return React.createElement(fallback);
            }
            return <div>You don't have permission to access this page.</div>;
        }

        return React.createElement(Component, props);
    };
}

/**
 * HOC to require any of the specified roles
 * @param Component - Component to wrap
 * @param requiredRoles - Array of required roles
 * @param fallback - Component to render if user doesn't have any of the roles
 * @returns Wrapped component
 */
export function withAnyRole<P extends object>(
    Component: React.ComponentType<P>,
    requiredRoles: string[],
    fallback?: React.ComponentType
) {
    return function AnyRoleProtectedComponent(props: P) {
        const { isAuthenticated, user, loading } = useAuthContext();

        if (loading) {
            return <div>Loading...</div>;
        }

        if (!isAuthenticated) {
            return <div>Please log in to access this page.</div>;
        }

        if (!user || !requiredRoles.includes(user.role)) {
            if (fallback) {
                return React.createElement(fallback);
            }
            return <div>You don't have permission to access this page.</div>;
        }

        return React.createElement(Component, props);
    };
}

// ============================================================================
// UTILITY COMPONENTS
// ============================================================================

/**
 * Component that renders children only if user is authenticated
 */
export interface AuthGuardProps {
    children: ReactNode;
    fallback?: ReactNode;
}

export const AuthGuard: React.FC<AuthGuardProps> = ({ children, fallback }) => {
    const { isAuthenticated, loading } = useAuthContext();

    if (loading) {
        return <div>Loading...</div>;
    }

    if (!isAuthenticated) {
        return <>{fallback || <div>Please log in to access this content.</div>}</>;
    }

    return <>{children}</>;
};

/**
 * Component that renders children only if user has specific role
 */
export interface RoleGuardProps {
    children: ReactNode;
    role: string;
    fallback?: ReactNode;
}

export const RoleGuard: React.FC<RoleGuardProps> = ({ children, role, fallback }) => {
    const { isAuthenticated, user, loading } = useAuthContext();

    if (loading) {
        return <div>Loading...</div>;
    }

    if (!isAuthenticated) {
        return <div>Please log in to access this content.</div>;
    }

    if (user?.role !== role) {
        return <>{fallback || <div>You don't have permission to access this content.</div>}</>;
    }

    return <>{children}</>;
};

/**
 * Component that renders children only if user has any of the specified roles
 */
export interface AnyRoleGuardProps {
    children: ReactNode;
    roles: string[];
    fallback?: ReactNode;
}

export const AnyRoleGuard: React.FC<AnyRoleGuardProps> = ({ children, roles, fallback }) => {
    const { isAuthenticated, user, loading } = useAuthContext();

    if (loading) {
        return <div>Loading...</div>;
    }

    if (!isAuthenticated) {
        return <div>Please log in to access this content.</div>;
    }

    if (!user || !roles.includes(user.role)) {
        return <>{fallback || <div>You don't have permission to access this content.</div>}</>;
    }

    return <>{children}</>;
};
